/**
 * @param {string} path -
 * @returns {string} - a full URL to an API endpoint
 */
function make(path) {
  return `${import.meta.env.UF_API_SERVER_ADDR}/api/${path}`;
}

/**
 * generic factory for generating API fetchers
 *
 * @param {string} method - the HTTP method to use
 * @param {object} options - additional fetch options
 * @returns {Function} - a function that fetches data from the API
 */
const createFetcher = (method, options) => async (path, body) => {
  const response = await fetch(make(path), {
    credentials: 'include',
    method,
    ...options,
    ...(body && { body: JSON.stringify(body) }),
  });

  if (!response.ok) {
    throw response;
  }

  if (response.status === 200) {
    return response.json();
  }

  return response;
};

/**
 * factory function for generating GET requests to the API
 *
 * @param {string} path - path to the endpoint
 * @returns {Promise<*>} - the response from the API
 */
const get = createFetcher('GET');

/**
 * factory function for generating PUT requests to the API
 *
 * @param {string} path - path to the endpoint
 * @param {object} body - the body of the request
 * @returns {Promise<*>} - the response from the API
 */
const put = createFetcher('PUT', {
  headers: { 'Content-Type': 'application/json' },
});

/**
 * factory function for generating DELETE requests to the API
 *
 * @param {string} path - path to the endpoint
 * @returns {Promise<*>} - the response from the API
 */
const destroy = createFetcher('DELETE');

/**
 * method for generating a URL to redirect to the login page
 *
 * @param {string} returnTo - the URL to redirect to after login
 * @returns {string} - the auth URL
 */
export const getLogin = (returnTo) =>
  make(`signin?reason=not_authenticated&redirect_to=${returnTo}`);

export default {
  /**
   * @param {string} org - organization key
   * @returns {Promise<Project[]>} - the dashboards for the organization
   */
  getDashboards: (org) => get(`organization/${org}/dashboards`),
  /**
   * @param {object} args -
   * @param {string} args.id - the dashboard ID
   * @param {string} args.org - organization key
   * @returns {Promise<{EmbedUrl: string}>} - the signed URL for the dashboard
   */
  getDashboardURL: ({ id, org }) => get(`organization/${org}/dashboard/${id}`),
  /**
   * @param {string} org - organization key
   * @returns {Promise<FeatureFlag[]>} - the feature flags for the organization
   */
  getFeatureFlags: (org) => get(`organization/${org}/feature_flags`),
  /**
   * @param {string} org - organization key
   * @returns {Promise<ProjectType[]>} - the project types for the organization
   */
  getProjectTypes: (org) => get(`project_types?org_key=${org}`),
  /**
   * @param {string} id - Q topic id
   * @returns {Promise<QnATopic>} - the QnA topic
   */
  getQnATopic: (id) => get(`topic/${id}`),
  /**
   * @returns {Promise<User>} - the current logged-in user
   */
  getUser: () => get('current_user'),
  /**
   * @param {string} user - user key
   * @returns {Promise<PortfolioInfo[]>} - the portfolios for the user
   */
  getUserPortfolios: (user) => get(`user/${user}/portfolios`),

  /**
   * @param {object} args -
   * @param {string} args.id - the dashboard ID
   * @param {string} args.org - organization key
   */
  removeDashboard: ({ id, org }) =>
    destroy(`organization/${org}/dashboard/${id}`),
  /**
   * @param {object} args -
   * @param {string} args.id - the dashboard ID
   * @param {string} args.org - organization key
   * @param {object} args.body - the new name for the dashboard
   * @returns {Promise<number>} - status 200 Successfully updated the dashboard name
   */
  updateDashboard: ({ id, org, body }) =>
    put(`organization/${org}/dashboard/${id}`, body),
  /**
   * @param {object} args -
   * @param {string} args.org - organization key
   * @param {string} args.project - project key
   * @returns {Promise<Project>} - the project
   */
  getProject: ({ org, project }) => get(`project/${org}/project/${project}`),
  /**
   * @param {string} org - organization key
   * @returns {Promise<Organization>} - the organization
   */
  getOrganization: (org) => get(`organization/${org}`),
};
